import {
    Alert,
    Anchor,
    Badge,
    Button,
    Group,
    Loader,
    Paper,
    Stack,
    Table,
    Text,
    Title,
} from '@mantine/core'
import {DateInput} from '@mantine/dates'
import {IconDownload} from '@tabler/icons-react'
import {Link, useNavigate} from '@tanstack/react-router'

import {lifecycleStatusColor} from 'src/theme/semantic-colors'
import {DetailPageBackLink} from 'src/ui/detail-page-back-link'

import {
    countEquipmentAssignments,
    countWorkerAssignments,
    formatAssignmentLines,
    formatCreatedAtLabel,
    formatReportDateLabel,
    formatReportStatusLabel,
    formatRequirementsLines,
    formatScheduleEndLabel,
    formatScheduleStartLabel,
} from './dispatch-report-fields'
import {downloadDispatchReportCsv} from './export-dispatch-report-csv'
import type {DispatchReportJob} from './schemas'
import {
    dispatchReportDateSearchValue,
    type DispatchReportSearch,
    parseDateInputValue,
    resolveDispatchReportDate,
} from './search'
import {useDispatchReport} from './use-dispatch-report'

function WorkersAndEquipmentCell({job}: {job: DispatchReportJob}) {
    const requirements = formatRequirementsLines(job)
    const assignments = formatAssignmentLines(job)

    return (
        <Stack gap={6}>
            <div>
                <Text size="xs" fw={600} c="dimmed" tt="uppercase">
                    Requirements
                </Text>
                {requirements.length === 0 ? (
                    <Text size="sm" c="dimmed">
                        —
                    </Text>
                ) : (
                    <Stack gap={2}>
                        {requirements.map((line) => (
                            <Text key={line} size="sm">
                                {line}
                            </Text>
                        ))}
                    </Stack>
                )}
            </div>
            <div>
                <Text size="xs" fw={600} c="dimmed" tt="uppercase">
                    Assignments
                </Text>
                {assignments.length === 0 ? (
                    <Text size="sm" c="dimmed">
                        —
                    </Text>
                ) : (
                    <Stack gap={2}>
                        {assignments.map((name) => (
                            <Text key={name} size="sm">
                                {name}
                            </Text>
                        ))}
                    </Stack>
                )}
            </div>
        </Stack>
    )
}

function SitesCell({job}: {job: DispatchReportJob}) {
    if (job.locations.length === 0) {
        return (
            <Text size="sm" c="dimmed">
                —
            </Text>
        )
    }

    return (
        <Stack gap={4}>
            {job.locations.map((location) => (
                <div key={`${location.name}-${location.notes ?? ''}`}>
                    <Text size="sm">{location.name}</Text>
                    {location.notes && (
                        <Text size="xs" c="dimmed" style={{whiteSpace: 'pre-wrap'}}>
                            {location.notes}
                        </Text>
                    )}
                </div>
            ))}
        </Stack>
    )
}

function JobNotesCell({job}: {job: DispatchReportJob}) {
    if (job.jobComments.length === 0) {
        return (
            <Text size="sm" c="dimmed">
                —
            </Text>
        )
    }

    return (
        <Stack gap={4}>
            {job.jobComments.map((comment) => (
                <Text key={comment.content} size="sm">
                    {comment.content}
                </Text>
            ))}
        </Stack>
    )
}

type DispatchReportPageProps = {
    search: DispatchReportSearch
}

function DispatchReportHeader({
    selectedDate,
    onReportDateChange,
    exportDisabled,
    onExport,
}: {
    selectedDate: Date
    onReportDateChange: (value: string | Date | null) => void
    exportDisabled: boolean
    onExport: () => void
}) {
    return (
        <Stack gap={4}>
            <DetailPageBackLink
                to="/dispatch"
                listLabel="dispatch board"
                backLabel="Back to dispatch board"
            />
            <Group justify="space-between" align="flex-end" wrap="wrap" gap="sm">
                <Group gap="xs" align="center" wrap="wrap">
                    <Title order={1}>Dispatch report —</Title>
                    <DateInput
                        aria-label="Report date"
                        value={selectedDate}
                        onChange={onReportDateChange}
                        valueFormat="MM/DD/YY"
                        w={130}
                        styles={{input: {fontWeight: 700}}}
                    />
                </Group>
                <Button
                    variant="default"
                    leftSection={<IconDownload size={16} stroke={1.5} />}
                    disabled={exportDisabled}
                    onClick={onExport}
                >
                    Export CSV
                </Button>
            </Group>
        </Stack>
    )
}

export function DispatchReportPage({search}: DispatchReportPageProps) {
    const navigate = useNavigate({from: '/dispatch/report'})
    const selectedDate = resolveDispatchReportDate(search)
    const reportDateParam = dispatchReportDateSearchValue(selectedDate)
    const {data: report, isLoading, isError, error, refetch} = useDispatchReport(reportDateParam)

    function handleReportDateChange(value: string | Date | null) {
        const parsed = parseDateInputValue(value)
        if (!parsed) {
            return
        }

        void navigate({
            search: {date: dispatchReportDateSearchValue(parsed)},
        })
    }

    if (isLoading) {
        return (
            <Stack gap="md">
                <DispatchReportHeader
                    selectedDate={selectedDate}
                    onReportDateChange={handleReportDateChange}
                    exportDisabled
                    onExport={() => {}}
                />
                <Group justify="center" py="xl">
                    <Loader />
                </Group>
            </Stack>
        )
    }

    if (isError || !report) {
        const message = error instanceof Error ? error.message : 'Failed to load dispatch report'
        return (
            <Stack gap="md">
                <DispatchReportHeader
                    selectedDate={selectedDate}
                    onReportDateChange={handleReportDateChange}
                    exportDisabled
                    onExport={() => {}}
                />
                <Alert color="red" title="Could not load dispatch report">
                    {message}
                </Alert>
                <Button onClick={() => void refetch()} variant="light">
                    Retry
                </Button>
            </Stack>
        )
    }

    return (
        <Stack gap="md">
            <DispatchReportHeader
                selectedDate={selectedDate}
                onReportDateChange={handleReportDateChange}
                exportDisabled={report.jobs.length === 0}
                onExport={() => downloadDispatchReportCsv(report)}
            />

            {report.jobs.length === 0 ? (
                <Paper withBorder p="md" radius="md">
                    <Text c="dimmed">
                        No jobs scheduled for {formatReportDateLabel(report.reportDate)}.
                    </Text>
                </Paper>
            ) : (
                <Paper withBorder radius="md" style={{overflowX: 'auto'}}>
                    <Table striped highlightOnHover withTableBorder withColumnBorders>
                        <Table.Thead>
                            <Table.Tr>
                                <Table.Th w={100}>Status</Table.Th>
                                <Table.Th w={96}>Created</Table.Th>
                                <Table.Th w={120}>Job</Table.Th>
                                <Table.Th>Start</Table.Th>
                                <Table.Th>End</Table.Th>
                                <Table.Th>PO</Table.Th>
                                <Table.Th>Customer</Table.Th>
                                <Table.Th>Sites</Table.Th>
                                <Table.Th>Workers</Table.Th>
                                <Table.Th>Equipment</Table.Th>
                                <Table.Th>Workers &amp; Equipment</Table.Th>
                                <Table.Th>Order notes</Table.Th>
                                <Table.Th>Job notes</Table.Th>
                            </Table.Tr>
                        </Table.Thead>
                        <Table.Tbody>
                            {report.jobs.map((job) => (
                                <Table.Tr key={job.id}>
                                    <Table.Td>
                                        <Badge
                                            variant="light"
                                            color={
                                                job.status === 'active'
                                                    ? lifecycleStatusColor('in_progress')
                                                    : lifecycleStatusColor('estimate')
                                            }
                                            size="md"
                                            fullWidth
                                        >
                                            {formatReportStatusLabel(job.status)}
                                        </Badge>
                                    </Table.Td>
                                    <Table.Td>{formatCreatedAtLabel(job.createdAt)}</Table.Td>
                                    <Table.Td>
                                        <Anchor
                                            component={Link}
                                            to="/jobs/$jobId"
                                            params={{jobId: job.id}}
                                        >
                                            {job.displayId}
                                        </Anchor>
                                    </Table.Td>
                                    <Table.Td>
                                        {formatScheduleStartLabel(job.startDate, job.startTime)}
                                    </Table.Td>
                                    <Table.Td>{formatScheduleEndLabel(job.endDate)}</Table.Td>
                                    <Table.Td>{job.poNumber ?? ''}</Table.Td>
                                    <Table.Td>
                                        <Anchor
                                            component={Link}
                                            to="/customers/$customerId"
                                            params={{customerId: job.customerId}}
                                        >
                                            {job.customerName}
                                        </Anchor>
                                    </Table.Td>
                                    <Table.Td>
                                        <SitesCell job={job} />
                                    </Table.Td>
                                    <Table.Td>{countWorkerAssignments(job)}</Table.Td>
                                    <Table.Td>{countEquipmentAssignments(job)}</Table.Td>
                                    <Table.Td>
                                        <WorkersAndEquipmentCell job={job} />
                                    </Table.Td>
                                    <Table.Td>
                                        {job.projectNotes ? (
                                            <Anchor
                                                component={Link}
                                                to="/projects/$projectId"
                                                params={{projectId: job.projectId}}
                                                size="sm"
                                                style={{whiteSpace: 'pre-wrap'}}
                                            >
                                                {job.projectNotes}
                                            </Anchor>
                                        ) : (
                                            <Text size="sm" c="dimmed">
                                                —
                                            </Text>
                                        )}
                                    </Table.Td>
                                    <Table.Td>
                                        <JobNotesCell job={job} />
                                    </Table.Td>
                                </Table.Tr>
                            ))}
                        </Table.Tbody>
                    </Table>
                </Paper>
            )}
        </Stack>
    )
}
